import { brands, BrandType } from 'noddi-types';
import { detectBrandFromLocation } from 'noddi-util';
import { PropsWithChildren, useLayoutEffect, useRef } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { BrandContext } from './brandContext';
import { createBrandStore, useCurrentBrand } from './brandStore';
import { BrandStore } from './brandStoreType';
import { syncBrandAnalytics } from './syncBrandAnalytics';

function useGetInitialBrand(): BrandType {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
  const brandFromUrl = searchParams.get('brand');

  if (brandFromUrl && Object.values(brands).includes(brandFromUrl as BrandType)) {
    return brandFromUrl as BrandType;
  }

  return detectBrandFromLocation(location.pathname, hostname);
}

export function NoddiBrandProvider({ children }: PropsWithChildren<unknown>) {
  const storeRef = useRef<BrandStore | undefined>(undefined);

  const initialBrand = useGetInitialBrand();

  // eslint-disable-next-line react-hooks/refs -- lazy one-time store init in a ref during render; the zustand-context pattern
  if (!storeRef.current) {
    storeRef.current = createBrandStore({
      initialBrand
    });
  }

  return (
    // eslint-disable-next-line react-hooks/refs -- store is created once above; the ref is the provider value
    <BrandContext.Provider value={storeRef.current}>
      <BrandAnalyticsTracker />
      {children}
    </BrandContext.Provider>
  );
}

// Persist rehydrate overwrites `currentBrand` without `setBrand`. Layout so
// TrackScreen's mount effect (child useEffect) sees brand on the first view.
function BrandAnalyticsTracker() {
  const { currentBrand } = useCurrentBrand();
  useLayoutEffect(() => {
    syncBrandAnalytics(currentBrand);
  }, [currentBrand]);
  return null;
}
